import { FC, useEffect } from 'react';
import {
  checkLockOnPage,
  ObjectType,
  useCheckForLockingError,
  useObjectLockQuery,
  UserFormDataType,
  useUserEditQuery,
  useQueryParams,
} from '@atlas-frontend/sdk';
import { path } from '../../paths';
import { useNavigateTo } from '../../paths/useNavigateTo';
import { Forbidden } from '../Forbidden';
import { useHasPermissionToEdit } from './useHasPermissionToEdit';
import { UserForm } from './UserForm';
import { useParams } from 'react-router-dom';

export const UserEdit = () => {
  const params = useParams<{ id: string }>();
  const userId = Number(params.id);
  const [{ segment = 'roles' }] = useQueryParams();

  if (Number.isNaN(userId)) {
    throw Error(`Expecting a numeric User ID, but got: ${params.id}`);
  }

  const checkForLockingError = useCheckForLockingError(
    'user',
    useNavigateTo(path.userView, { id: userId, segment }),
  );

  const { data: user, error } = useUserEditQuery({ id: userId, lock: true });

  useEffect(() => {
    if (error) {
      checkForLockingError(error);
    }
  }, [error, checkForLockingError]);

  if (!user) {
    return null;
  }

  return <UserEditInner user={user} />;
};

const UserEditInner: FC<{ user: UserFormDataType }> = (props) => {
  const { user } = props;
  const [{ segment = 'roles' }] = useQueryParams();
  const { data: lockData } = useObjectLockQuery({
    objectId: user.id,
    objectType: ObjectType.User,
  });
  const navigateToUserView = useNavigateTo(path.userView, { id: user.id });
  const navigateToUserViewSegment = () => navigateToUserView({ segment });
  checkLockOnPage(lockData, navigateToUserViewSegment);

  if (!useHasPermissionToEdit(user)) {
    return <Forbidden />;
  }

  return (
    <UserForm
      mode="edit"
      id={user.id}
      defaultValues={user}
      onClickCancel={navigateToUserViewSegment}
    />
  );
};
